import { Label } from "layout/typography/Label";
import {
  COPAY_FROM_XRAY_LINE_HELP,
  COPAY_FROM_XRAY_LINE_LABEL,
} from "pages/EligibilityPage/cptGroupWording";
import { MODALITY_CHOICES } from "pages/EligibilityPage/serviceTypeRuleWording";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { MultiSelect } from "primereact/multiselect";
import { useEffect, useState } from "react";
import styled from "styled-components";
import { ISelectionPriority } from "types/BenefitSelection/BenefitSelection";
import { PracticeTypeRuleModality } from "types/PracticeTypeSuggestion/PracticeTypeSuggestion";

import { KeywordChips, withPending } from "../KeywordChips";
import { Surface } from "../Surface";
import {
  ISelectionRuleForm,
  NETWORK_OPTIONS,
  OTHERWISE_HELP,
  PHYSICIAN_SOURCES,
  RuleScope,
  TIE_BREAK_HELP,
  TIE_BREAK_OPTIONS,
  emptyPriority,
  lineRoleLabel,
  normaliseKeywords,
  validateRule,
} from "./selectionRules";

const PriorityBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  padding: 0.875rem;
  background: var(--surface-50);
  border-radius: 10px;
`;

const PriorityTitle = styled.span`
  font-weight: 700;
  color: var(--primary-color);
`;

const OtherwiseBox = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  padding: 0.75rem 0.875rem;
  border: 1px dashed var(--surface-300);
  border-radius: 10px;
`;

interface ISelectionRuleDialogProps {
  visible: boolean;
  scope: RuleScope;
  /** The rule being edited, or a blank one when adding. */
  value: ISelectionRuleForm;
  isNew: boolean;
  payerOptions: Array<{ label: string; value: number }>;
  loadingPayers: boolean;
  /** Line types the card knows (the vocabulary's roles), for the physician tab. */
  knownRoles: string[];
  onSave: (rule: ISelectionRuleForm) => void;
  onHide: () => void;
}

/**
 * CLE-2802 — one selection rule, edited in isolation: payers, plan, network, and its ordered
 * priorities. Edits are held here and handed back only on Save, so Cancel really discards them.
 *
 * ⚠️ ERRORS SHOW ON THE SAVE ATTEMPT, NOT BEFORE — a dialog that opens complaining about a form
 * nobody has filled in teaches an editor to read past its warnings (the filter dialog's rule).
 */
export function SelectionRuleDialog({
  visible,
  scope,
  value,
  isNew,
  payerOptions,
  loadingPayers,
  knownRoles,
  onSave,
  onHide,
}: ISelectionRuleDialogProps) {
  const [draft, setDraft] = useState<ISelectionRuleForm>(value);
  /** Keyword text typed and not yet a chip — see `KeywordChips`. Never silently discarded. */
  const [pending, setPending] = useState<Record<string, string>>({});
  const [attempted, setAttempted] = useState(false);

  useEffect(() => {
    if (!visible) return;
    setDraft(value);
    setPending({});
    setAttempted(false);
  }, [visible, value]);

  const isPhysician = scope === "professional";

  const setPriority = (index: number, patch: Partial<ISelectionPriority>) =>
    setDraft((current) => ({
      ...current,
      priorities: current.priorities.map((priority, i) =>
        i === index ? { ...priority, ...patch } : priority
      ),
    }));

  /** The rule as it really stands, including keyword text still sitting in an input. */
  const committed: ISelectionRuleForm = {
    ...draft,
    planKeywords: normaliseKeywords(
      withPending(draft.planKeywords, pending.plan)
    ),
    priorities: draft.priorities.map((priority, index) => ({
      ...priority,
      matchKeywords: normaliseKeywords(
        withPending(priority.matchKeywords, pending[`match-${index}`])
      ),
      excludeKeywords: normaliseKeywords(
        withPending(priority.excludeKeywords, pending[`exclude-${index}`])
      ),
    })),
  };

  const errors = validateRule(committed);
  const valid = Object.keys(errors).length === 0;

  let title = "Edit rule";
  if (isNew) title = draft.isDefault ? "Add Default" : "Add rule";
  else if (draft.isDefault) title = "Edit Default";

  const roleOptions = [
    { label: "None — match on words only", value: "" },
    ...knownRoles.map((role) => ({ label: lineRoleLabel(role), value: role })),
  ];

  const sourceOptions = (current: string | null) =>
    current && !PHYSICIAN_SOURCES.some((option) => option.value === current)
      ? [...PHYSICIAN_SOURCES, { label: current, value: current as any }]
      : PHYSICIAN_SOURCES;

  // `pending` keys are positional, so removing a priority drops the ones at and after it rather than
  // attaching one priority's unfinished text to its neighbour.
  const removePriority = (index: number) => {
    setDraft((current) => ({
      ...current,
      priorities: current.priorities.filter((_, i) => i !== index),
    }));
    setPending((current) =>
      Object.fromEntries(
        Object.entries(current).filter(([key]) => {
          const match = /-(\d+)$/.exec(key);
          return !match || Number(match[1]) < index;
        })
      )
    );
  };

  return (
    <Dialog
      header={title}
      visible={visible}
      onHide={onHide}
      style={{ width: "min(52rem, 95vw)" }}
      data-testid="selectionRuleDialog"
      footer={
        <div className="flex justify-content-end gap-2">
          <Button
            type="button"
            label="Cancel"
            className="p-button-text p-button-sm"
            data-testid="selectionRuleDialog.cancel"
            onClick={onHide}
          />
          <Button
            type="button"
            label="Apply"
            className="p-button-sm"
            data-testid="selectionRuleDialog.save"
            onClick={() => {
              setAttempted(true);
              if (valid) onSave(committed);
            }}
          />
        </div>
      }
    >
      <Surface className="flex flex-column gap-4">
        {!draft.isDefault && (
          <div className="grid">
            <div className="col-12 md:col-7 flex flex-column gap-1">
              <Label htmlFor="selectionRuleDialog.payerIds">
                Applies to payers
              </Label>
              <MultiSelect
                id="selectionRuleDialog.payerIds"
                data-testid="selectionRuleDialog.payerIds"
                className="w-full"
                display="chip"
                filter
                options={payerOptions}
                disabled={loadingPayers}
                value={draft.payerIds}
                placeholder="Select payers…"
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    payerIds: event.value ?? [],
                  }))
                }
              />
            </div>
            <div className="col-12 md:col-5 flex flex-column gap-1">
              <Label htmlFor="selectionRuleDialog.planKeywords">
                Plan name contains (optional)
              </Label>
              <KeywordChips
                id="selectionRuleDialog.planKeywords"
                testId="selectionRuleDialog.planKeywords"
                value={draft.planKeywords}
                placeholder="any plan"
                onChange={(values, text) => {
                  setDraft((current) => ({ ...current, planKeywords: values }));
                  setPending((current) => ({ ...current, plan: text }));
                }}
              />
            </div>
            <div className="col-12 md:col-7 flex flex-column gap-1">
              <Label htmlFor="selectionRuleDialog.procedureGroups">
                CPT groups (optional)
              </Label>
              <MultiSelect
                inputId="selectionRuleDialog.procedureGroups"
                data-testid="selectionRuleDialog.procedureGroups"
                className="w-full"
                display="chip"
                options={MODALITY_CHOICES}
                placeholder="Every estimate"
                value={draft.procedureGroups}
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    procedureGroups: (event.value ??
                      []) as PracticeTypeRuleModality[],
                  }))
                }
              />
              <small className="text-color-secondary">
                Only for estimates with a CPT code in one of these groups — for
                ultrasounds on this plan, say, leaving its surgeries alone.
              </small>
            </div>
            {!isPhysician && (
              <div className="col-12 md:col-5 flex align-items-start gap-2 pt-4">
                <Checkbox
                  inputId="selectionRuleDialog.copayFromXrayLine"
                  data-testid="selectionRuleDialog.copayFromXrayLine"
                  checked={draft.copayFromXrayLine}
                  onChange={(event) =>
                    setDraft((current) => ({
                      ...current,
                      copayFromXrayLine: event.checked === true,
                    }))
                  }
                />
                <div className="flex flex-column gap-1">
                  <Label htmlFor="selectionRuleDialog.copayFromXrayLine">
                    {COPAY_FROM_XRAY_LINE_LABEL}
                  </Label>
                  <small className="text-color-secondary">
                    {COPAY_FROM_XRAY_LINE_HELP}
                  </small>
                </div>
              </div>
            )}
            {attempted && errors.scope && (
              <div className="col-12">
                <span
                  className="text-sm text-red-600"
                  data-testid="selectionRuleDialog.scopeError"
                >
                  {errors.scope}
                </span>
              </div>
            )}
          </div>
        )}

        <div className="flex align-items-center gap-3">
          <Label htmlFor="selectionRuleDialog.network">Network</Label>
          <Dropdown
            inputId="selectionRuleDialog.network"
            data-testid="selectionRuleDialog.network"
            options={NETWORK_OPTIONS}
            value={draft.networkStatus}
            onChange={(event) =>
              setDraft((current) => ({
                ...current,
                networkStatus: event.value,
              }))
            }
          />
        </div>

        <div className="flex flex-column gap-3">
          <span className="font-semibold">
            Priorities — checked in order; the first that finds a line is used
          </span>

          {draft.priorities.map((priority, index) => {
            const n = index + 1;
            const prefix = `selectionRuleDialog.priority.${index}`;
            return (
              // Priorities have no identity beyond their position, which is what they are.
              // eslint-disable-next-line react/no-array-index-key
              <PriorityBox key={index} data-testid={prefix}>
                <div className="flex justify-content-between align-items-center">
                  <PriorityTitle>{`Priority ${n}`}</PriorityTitle>
                  {/* CLE-2895 — the last priority can go too: the implicit catch-all answers for a rule with none. */}
                  <Button
                    type="button"
                    icon="pi pi-trash"
                    label="Remove"
                    className="p-button-text p-button-sm p-button-danger"
                    aria-label={`Remove priority ${n}`}
                    data-testid={`${prefix}.remove`}
                    onClick={() => removePriority(index)}
                  />
                </div>

                {isPhysician && (
                  <div className="flex align-items-center gap-2 flex-wrap">
                    <Label htmlFor={`${prefix}.source`}>Service line</Label>
                    <Dropdown
                      inputId={`${prefix}.source`}
                      data-testid={`${prefix}.source`}
                      options={sourceOptions(priority.source)}
                      value={priority.source ?? "SURGICAL"}
                      onChange={(event) =>
                        setPriority(index, { source: event.value })
                      }
                    />
                    <Label htmlFor={`${prefix}.lineRole`} className="ml-3">
                      Line type
                    </Label>
                    <Dropdown
                      inputId={`${prefix}.lineRole`}
                      data-testid={`${prefix}.lineRole`}
                      options={
                        priority.lineRole &&
                        !knownRoles.includes(priority.lineRole)
                          ? [
                              ...roleOptions,
                              {
                                label: lineRoleLabel(priority.lineRole),
                                value: priority.lineRole,
                              },
                            ]
                          : roleOptions
                      }
                      value={priority.lineRole ?? ""}
                      onChange={(event) =>
                        setPriority(index, { lineRole: event.value || null })
                      }
                    />
                  </div>
                )}

                <div className="grid">
                  <div className="col-12 md:col-6 flex flex-column gap-1">
                    <Label htmlFor={`${prefix}.match`}>
                      Contains any of (blank = any line)
                    </Label>
                    <KeywordChips
                      id={`${prefix}.match`}
                      testId={`${prefix}.match`}
                      value={priority.matchKeywords}
                      onChange={(values, text) => {
                        setPriority(index, { matchKeywords: values });
                        setPending((current) => ({
                          ...current,
                          [`match-${index}`]: text,
                        }));
                      }}
                    />
                  </div>
                  <div className="col-12 md:col-6 flex flex-column gap-1">
                    <Label htmlFor={`${prefix}.exclude`}>
                      Doesn&apos;t contain
                    </Label>
                    <KeywordChips
                      id={`${prefix}.exclude`}
                      testId={`${prefix}.exclude`}
                      value={priority.excludeKeywords}
                      onChange={(values, text) => {
                        setPriority(index, { excludeKeywords: values });
                        setPending((current) => ({
                          ...current,
                          [`exclude-${index}`]: text,
                        }));
                      }}
                    />
                  </div>
                  <small
                    className="col-12 -mt-2 text-color-secondary"
                    data-testid={`${prefix}.keywordHint`}
                  >
                    Words are matched, ignoring case, anywhere in a line&apos;s
                    messages, coverage description, PPO network name or place of
                    service (Office, Outpatient Hospital).
                  </small>
                </div>

                <div className="flex align-items-center gap-2">
                  <label htmlFor={`${prefix}.tieBreak`}>
                    If several lines are found, select the
                  </label>
                  <Dropdown
                    inputId={`${prefix}.tieBreak`}
                    data-testid={`${prefix}.tieBreak`}
                    options={TIE_BREAK_OPTIONS}
                    value={priority.tieBreak}
                    onChange={(event) =>
                      setPriority(index, { tieBreak: event.value })
                    }
                  />
                </div>
                {TIE_BREAK_HELP[priority.tieBreak] && (
                  <small
                    className="text-color-secondary"
                    data-testid={`${prefix}.tieBreakHelp`}
                  >
                    {TIE_BREAK_HELP[priority.tieBreak]}
                  </small>
                )}
              </PriorityBox>
            );
          })}

          <OtherwiseBox data-testid="selectionRuleDialog.otherwise">
            <span className="font-bold text-color-secondary">Otherwise</span>
            <span className="text-color-secondary">
              {OTHERWISE_HELP[scope]}
            </span>
          </OtherwiseBox>

          <div>
            <Button
              type="button"
              label="+ Add priority"
              className="p-button-outlined p-button-sm"
              data-testid="selectionRuleDialog.addPriority"
              onClick={() =>
                setDraft((current) => ({
                  ...current,
                  priorities: [...current.priorities, emptyPriority(scope)],
                }))
              }
            />
          </div>
        </div>
      </Surface>
    </Dialog>
  );
}
